import { useState } from 'react';
import { toast } from 'sonner';
import { Lock, KeyRound, Sparkles } from 'lucide-react';
import { EtatJeu, GameData, Maj, MotCle } from '../game/types';
import { deobf, verifierMot } from '../game/logic';
import { Petales } from '../components/Petales';

interface Props {
  data: GameData;
  etat: EtatJeu;
  maj: Maj;
}

const NOMS: Record<string, string> = { motA: 'Première serrure', motB: 'Deuxième serrure', motC: 'Troisième serrure' };

export default function Serrures({ data, etat, maj }: Props) {
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [jokerOuvertPour, setJokerOuvertPour] = useState<string | null>(null);
  const [revelation, setRevelation] = useState<MotCle | null>(null);
  const [occupied, setOccupied] = useState(false);

  const nbCollectes = (groupe: string) =>
    etat.indicesCollectes.filter((id) => data.invites.find((i) => i.id === id)?.groupe === groupe).length;

  const essayer = async (mot: MotCle) => {
    const saisie = (valeurs[mot.id] || '').trim();
    if (!saisie || occupied) return;
    setOccupied(true);
    const ok = await verifierMot(saisie, mot);
    setOccupied(false);
    if (ok) {
      const estDerniere = etat.serruresOuvertes.length === 2;
      maj((e) => ({
        ...e,
        serruresOuvertes: [...e.serruresOuvertes, mot.id],
        termineLe: estDerniere ? Date.now() : e.termineLe,
      }));
      if (!estDerniere) setRevelation(mot);
    } else {
      const erreurs = (etat.erreursSerrure[mot.id] || 0) + 1;
      const motAffiche = deobf(mot.reponseObf);
      const lettresActuelles = etat.lettresRevelees[mot.id] || 0;
      const reveler = erreurs % 3 === 0 && lettresActuelles < motAffiche.replace(/-/g, '').length - 1;
      maj((e) => ({
        ...e,
        erreursSerrure: { ...e.erreursSerrure, [mot.id]: erreurs },
        lettresRevelees: reveler ? { ...e.lettresRevelees, [mot.id]: lettresActuelles + 1 } : e.lettresRevelees,
        penalitesSec: reveler ? e.penalitesSec + 60 : e.penalitesSec,
      }));
      if (reveler) toast.error('Trois refus — une lettre se révèle (+60 s).');
      else toast.error('La serrure résiste. Réessayez.');
    }
  };

  const utiliserJoker = (mot: MotCle) => {
    const manquant = data.invites.find(
      (i) => i.groupe === mot.groupe && !etat.indicesCollectes.includes(i.id)
    );
    if (!manquant) return;
    maj((e) => ({
      ...e,
      indicesCollectes: [...e.indicesCollectes, manquant.id],
      carnet: { ...e.carnet, [manquant.id]: '(débloqué par le joker)' },
      jokerUtilise: true,
      penalitesSec: e.penalitesSec + 120,
    }));
    setJokerOuvertPour(null);
    toast.success(`L'indice de ${manquant.prenom} vous est accordé (+120 s).`);
  };

  const affichageLettres = (mot: MotCle) => {
    const n = etat.lettresRevelees[mot.id] || 0;
    if (!n) return null;
    const rep = deobf(mot.reponseObf);
    let compte = 0;
    return rep
      .split('')
      .map((c) => {
        if (c === '-') return '-';
        compte += 1;
        return compte <= n ? c : '_';
      })
      .join(' ');
  };

  return (
    <div className="space-y-5">
      <div className="anim-apparition">
        <p className="petit-cap">Acte III — Les trois mots-clés</p>
        <p className="text-sm text-encre-clair mt-2 leading-relaxed">
          Une serrure se tente dès {data.motscles[0]?.indicesGuess ?? 2} indices recueillis dans son groupe
          — mais chaque indice supplémentaire éclaire le mot.
        </p>
      </div>

      {data.motscles.map((mot) => {
        const ouverte = etat.serruresOuvertes.includes(mot.id);
        const collecte = nbCollectes(mot.groupe);
        const active = collecte >= mot.indicesGuess;
        const lettres = affichageLettres(mot);

        if (ouverte) {
          return (
            <div key={mot.id} className="border border-succes rounded-lg px-5 py-5 bg-white" data-testid={`serrure-card-${mot.id}`}>
              <p className="petit-cap text-succes">{NOMS[mot.id]} — ouverte</p>
              <p className="font-script text-5xl text-encre mt-2">{deobf(mot.reponseObf)}</p>
              <p className="text-sm text-texte italic mt-2">{mot.texteRevelation}</p>
            </div>
          );
        }

        if (!active) {
          return (
            <div key={mot.id} className="border border-petale rounded-lg px-5 py-5 bg-brume/60" data-testid={`serrure-card-${mot.id}`}>
              <p className="petit-cap flex items-center gap-2">
                <Lock size={14} /> {NOMS[mot.id]} — groupe {mot.groupe}
              </p>
              <p className="text-sm text-texte mt-2">
                {collecte}/{mot.indicesRequis} indices recueillis. Encore {mot.indicesGuess - collecte} pour
                pouvoir tenter le mot — cherchez les tatoués du groupe {mot.groupe}.
              </p>
              <div className="flex gap-1.5 mt-3">
                {Array.from({ length: mot.indicesRequis }, (_, i) => (
                  <span key={i} className={`h-2 flex-1 rounded-full ${i < collecte ? 'bg-encre' : 'bg-white border border-petale'}`} />
                ))}
              </div>
              {!etat.jokerUtilise && (
                <div className="mt-4">
                  {jokerOuvertPour === mot.id ? (
                    <div className="space-y-2">
                      <p className="text-sm text-texte" data-testid="joker-avertissement">
                        Le joker débloque l'indice manquant de ce groupe contre <strong>+120 s</strong> de
                        pénalité. Un seul usage pour toute la partie.
                      </p>
                      <div className="flex gap-2">
                        <button data-testid="joker-valider-btn" className="btn-secondaire flex-1" onClick={() => utiliserJoker(mot)}>
                          Valider (+120 s)
                        </button>
                        <button data-testid="joker-annuler-btn" className="btn-secondaire flex-1" onClick={() => setJokerOuvertPour(null)}>
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid={`joker-ouvrir-${mot.id}`}
                      onClick={() => setJokerOuvertPour(mot.id)}
                      className="min-h-[44px] text-sm text-encre-clair underline underline-offset-4"
                    >
                      Un porteur introuvable ? Utiliser le joker
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        }

        return (
          <div key={mot.id} className="border border-encre rounded-lg px-5 py-5 bg-white" data-testid={`serrure-card-${mot.id}`}>
            <p className="petit-cap flex items-center gap-2">
              <KeyRound size={14} /> {NOMS[mot.id]} — groupe {mot.groupe}
            </p>
            <p className="text-sm text-texte mt-2">{mot.sens}.</p>
            {collecte < mot.indicesRequis && (
              <p className="text-xs text-encre-clair mt-1" data-testid={`guess-note-${mot.id}`}>
                {collecte}/{mot.indicesRequis} indices — vous pouvez déjà tenter votre chance, ou{' '}
                {mot.indicesRequis - collecte === 1
                  ? 'recueillir le dernier'
                  : `recueillir les ${mot.indicesRequis - collecte} restants`}.
              </p>
            )}
            {lettres && (
              <p className="mt-2 font-semibold text-encre tracking-widest tabular-nums" data-testid={`lettres-revelees-${mot.id}`}>
                {lettres}
              </p>
            )}
            <input
              data-testid={`serrure-input-${mot.id}`}
              value={valeurs[mot.id] || ''}
              onChange={(e) => setValeurs((v) => ({ ...v, [mot.id]: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && essayer(mot)}
              placeholder="Votre mot-clé"
              autoCapitalize="characters"
              className="mt-3 w-full min-h-[52px] px-4 rounded-lg border border-encre bg-white text-lg text-center tracking-wide text-texte placeholder:text-encre-clair/60"
            />
            <button
              data-testid={`serrure-valider-${mot.id}`}
              className="btn-principal mt-3"
              disabled={occupied || !(valeurs[mot.id] || '').trim()}
              onClick={() => essayer(mot)}
            >
              Ouvrir la serrure
            </button>
          </div>
        );
      })}

      {revelation && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center px-8 text-center anim-apparition" data-testid="revelation-overlay">
          <Petales nombre={16} />
          <Sparkles className="text-olive" size={28} />
          <p className="petit-cap mt-4">Serrure ouverte</p>
          <p className="font-script text-7xl text-encre mt-4 leading-tight">{deobf(revelation.reponseObf)}</p>
          <p className="text-texte italic leading-relaxed mt-6 max-w-xs">{revelation.texteRevelation}</p>
          <button data-testid="poursuivre-btn" className="btn-principal mt-10 max-w-xs" onClick={() => setRevelation(null)}>
            Poursuivre la quête
          </button>
        </div>
      )}
    </div>
  );
}
